import { useState, useEffect } from "react";
import { withRouter, Link } from "react-router-dom";
import AuthPageLayout from "../../layouts/VerticalLayouts/AuthPageLayout";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CardBody,
  Card,
  Input,
  Label,
  Form,
  Button,
  Spinner,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";
import ResetPWSuccessUI from "./ResetPWSuccessUI";
import { resetPassword } from "../../store/actions";
import {
  PASSWORD_RULES,
  passwordChecks,
  passwordSchema,
} from "../../helpers/passwordRules";

const ResetPasswordPage = (props) => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", {
    page: t("auth.resetPassword.pageTitle"),
  });

  // password registry validation

  const dispatch = useDispatch();
  const urlToken = props.match.params.token;
  const { isPasswordChanged, resetError, errorCount, loading } = useSelector(
    (state) => ({
      isPasswordChanged: state.ResetPassword.resetSuccess,
      resetError: state.ResetPassword.resetError,
      errorCount: state.ResetPassword.errorCount,
      loading: state.ResetPassword.loading,
    })
  );

  // Show error toast when resetError changes
  useEffect(() => {
    if (resetError && errorCount > 0) {
      // Extract error message from various possible error formats
      let errorMessage = t("auth.resetPassword.error");

      if (resetError.data) {
        if (resetError.data.response?.data?.message) {
          errorMessage = translateApiError(resetError.data.response.data);
        } else if (resetError.data.message) {
          errorMessage = translateApiError(resetError.data);
        } else if (typeof resetError.data === "string") {
          errorMessage = translateApiError(resetError.data);
        }
      } else if (typeof resetError === "string") {
        errorMessage = translateApiError(resetError);
      }

      // Dismiss any existing toasts and show new one
      toast.dismiss();
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    // Only when a new error arrives, not when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCount, resetError]);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      token: urlToken,
      newPassword: "",
      confirmPassword: "",
    },

    validationSchema: Yup.object({
      newPassword: passwordSchema(t).required(
        t("auth.activate.validation.passwordRequired")
      ),
      confirmPassword: Yup.string()
        .required(t("auth.validation.reenterNewPassword"))
        .when("newPassword", {
          is: (val) => (val && val.length > 0 ? true : false),
          then: Yup.string().oneOf(
            [Yup.ref("newPassword")],
            t("auth.validation.passwordMismatch")
          ),
        }),
    }),

    onSubmit: (values) => {
      dispatch(resetPassword(values));
    },
  });

  // to-do refactor this
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <AuthPageLayout>
      <ToastContainer />
      <Card className="mt-4">
        <CardBody
          style={{
            paddingTop: "28px",
            paddingBottom: "28px",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          {isPasswordChanged ? (
            <ResetPWSuccessUI />
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-primary fw-semibold mb-2 auth-form-heading">
                  {t("auth.resetPassword.heading")}
                </h3>
                <p className="text-muted mb-0 auth-form-subheading">
                  {t("auth.changePasswordSubheading")}
                </p>
              </div>

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="mb-3">
                  <Label
                    htmlFor="newPassword"
                    className="form-label fw-semibold auth-form-label"
                  >
                    {t("auth.fields.newPassword")}
                    <span className="text-danger">*</span>
                  </Label>
                  <div className="position-relative">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      className="form-control auth-form-input"
                      placeholder={t(
                        "auth.resetPassword.newPasswordPlaceholder"
                      )}
                      type={showNewPassword ? "text" : "password"}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.newPassword || ""}
                      invalid={
                        validation.touched.newPassword &&
                        validation.errors.newPassword
                          ? true
                          : false
                      }
                    />
                    <button
                      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-dark"
                      type="button"
                      onClick={toggleNewPassword}
                      style={{ padding: "0.47rem 0.75rem" }}
                    >
                      <i
                        className={
                          showNewPassword
                            ? "ri-eye-off-fill align-middle"
                            : "ri-eye-fill align-middle"
                        }
                      ></i>
                    </button>
                  </div>
                  {validation.touched.newPassword &&
                  validation.errors.newPassword &&
                  validation.values.newPassword ? (
                    <div className="text-danger mt-1 auth-form-error">
                      <p className="mb-1">
                        {t("auth.passwordRequirements.title")}
                      </p>
                      <ul
                        className="mb-0"
                        style={{ paddingLeft: "20px", fontSize: "12px" }}
                      >
                        {PASSWORD_RULES.map((rule) => (
                          <li
                            key={rule}
                            className={
                              passwordChecks(validation.values.newPassword)[
                                rule
                              ]
                                ? "text-success"
                                : ""
                            }
                          >
                            {t(`auth.passwordRequirements.${rule}`)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                <div className="mb-4">
                  <Label
                    htmlFor="confirmPassword"
                    className="form-label fw-semibold auth-form-label"
                  >
                    {t("auth.resetPassword.confirmPassword")}
                    <span className="text-danger">*</span>
                  </Label>
                  <div className="position-relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control auth-form-input"
                      placeholder={t(
                        "auth.resetPassword.confirmPasswordPlaceholder"
                      )}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.confirmPassword || ""}
                      invalid={
                        validation.touched.confirmPassword &&
                        validation.errors.confirmPassword
                          ? true
                          : false
                      }
                    />
                    <button
                      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-dark"
                      type="button"
                      onClick={toggleConfirmPassword}
                      style={{ padding: "0.47rem 0.75rem" }}
                    >
                      <i
                        className={
                          showConfirmPassword
                            ? "ri-eye-off-fill align-middle"
                            : "ri-eye-fill align-middle"
                        }
                      ></i>
                    </button>
                  </div>
                  {validation.touched.confirmPassword &&
                  validation.errors.confirmPassword ? (
                    <div className="text-danger mt-1 auth-form-error">
                      {validation.errors.confirmPassword}
                    </div>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Button
                    color="primary"
                    type="submit"
                    className="w-100 fw-semibold auth-form-button"
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      t("auth.resetPassword.submit")
                    )}
                  </Button>
                </div>

                {/* Remember Password link */}
                <div className="text-center">
                  <span className="text-dark auth-form-footer-text">
                    {t("auth.links.rememberPassword")}
                  </span>
                  <Link
                    to="/login"
                    className="text-primary text-decoration-underline auth-form-footer-text"
                  >
                    {t("auth.actions.signIn")}
                  </Link>
                </div>
              </Form>
            </>
          )}
        </CardBody>
      </Card>
    </AuthPageLayout>
  );
};

export default withRouter(ResetPasswordPage);
